// The Research list's query, kept in the URL (spec §5.1): a refresh, a
// shared link and Back all land on the same list. Every value is checked
// against what the API takes (backend/investing/views.py) before it's sent,
// so a hand-edited link still lists instead of answering 400.
import { INVESTING_SLUG_RE } from "../../../services/api";
import { VERDICTS } from "./format";

/** The filters, in the order the page shows them. `q` is the search box. */
export const FILTER_KEYS = ["q", "verdict", "thesis", "sector", "researched", "proposed"];

// The API's bounds (constants.py Q_MAX, SECTOR_MAX, SLUG_MAX).
const Q_MAX = 100;
const SECTOR_MAX = 80;
const SLUG_MAX = 60;

/**
 * The screen's sectors: Morningstar's labels, which the Russell 1000
 * screen imports (mono-mcp-workflows investing.md, `morningstarSectorLabel`).
 * The API has no list of the sectors in use, so these are the choices; the
 * API matches them ignoring case.
 */
export const SECTORS = [
  "Basic Materials",
  "Communication Services",
  "Consumer Cyclical",
  "Consumer Defensive",
  "Energy",
  "Financial Services",
  "Healthcare",
  "Industrials",
  "Real Estate",
  "Technology",
  "Utilities",
];

const FLAGS = new Set(["yes", "no"]);
const PLAIN_ORDERINGS = new Set(["symbol", "market_cap", "last_researched_at"]);
const isSlug = (value) => value.length <= SLUG_MAX && INVESTING_SLUG_RE.test(value);

function validOrdering(raw) {
  const key = raw.startsWith("-") ? raw.slice(1) : raw;
  if (PLAIN_ORDERINGS.has(key)) return true;
  return key.startsWith("score:") && isSlug(key.slice("score:".length));
}

/** The list's query from the URL; anything the API would refuse reads as "". */
export function readQuery(searchParams) {
  const text = (name, max) => {
    const value = (searchParams.get(name) || "").trim();
    return value.length <= max && !value.includes("\u0000") ? value : "";
  };
  const verdict = searchParams.get("verdict") || "";
  const thesis = searchParams.get("thesis") || "";
  const researched = searchParams.get("researched") || "";
  const proposed = searchParams.get("proposed") || "";
  const ordering = searchParams.get("ordering") || "";
  return {
    verdict: VERDICTS.some((v) => v.value === verdict) ? verdict : "",
    thesis: isSlug(thesis) ? thesis : "",
    sector: text("sector", SECTOR_MAX),
    researched: FLAGS.has(researched) ? researched : "",
    proposed: FLAGS.has(proposed) ? proposed : "",
    q: text("q", Q_MAX),
    ordering: validOrdering(ordering) ? ordering : "",
  };
}

/** Is any filter set (the order isn't a filter)? */
export function hasFilters(query) {
  return FILTER_KEYS.some((key) => Boolean(query[key]));
}

const scoresThesis = (ordering) => /^-?score:/.test(ordering || "");

/**
 * The URL after one filter changes: that filter set (or removed when
 * empty), and the page dropped — in ONE write, so the list never fetches
 * the old page under the new filter (usePageParam's rule). Choosing
 * another thesis drops an ordering by the old thesis's score.
 */
export function withFilter(searchParams, name, value) {
  const next = new URLSearchParams(searchParams);
  if (value) next.set(name, value);
  else next.delete(name);
  next.delete("page");
  if (name === "thesis") {
    const ordering = next.get("ordering");
    if (scoresThesis(ordering) && ordering.replace(/^-?score:/, "") !== value) {
      next.delete("ordering");
    }
  }
  return next;
}

/** The URL with every filter and the page cleared; a plain ordering stays. */
export function withoutFilters(searchParams) {
  const next = new URLSearchParams(searchParams);
  FILTER_KEYS.forEach((key) => next.delete(key));
  next.delete("page");
  if (scoresThesis(next.get("ordering"))) next.delete("ordering");
  return next;
}

const ORDERING_LABELS = {
  symbol: "Symbol, A–Z",
  "-symbol": "Symbol, Z–A",
  "-market_cap": "Market cap, largest first",
  market_cap: "Market cap, smallest first",
  "-last_researched_at": "Last researched, newest first",
  last_researched_at: "Last researched, oldest first",
};

/**
 * The sort choices: symbol (the API's default, sent as no ordering),
 * market cap and last researched; with a thesis chosen, its score, highest
 * first. An ordering the URL carries that isn't offered is added, so the
 * select always shows what the list is sorted by.
 * @param {{thesis: string, ordering: string}} query
 * @param {Record<string, string>} [titles] thesis slug → title
 */
export function orderingOptions({ thesis, ordering }, titles = {}) {
  const name = (slug) => titles[slug] || slug;
  const options = [
    { value: "", label: ORDERING_LABELS.symbol },
    { value: "-market_cap", label: ORDERING_LABELS["-market_cap"] },
    { value: "-last_researched_at", label: ORDERING_LABELS["-last_researched_at"] },
  ];
  if (thesis) {
    // The Thesis filter beside it names the thesis (a long title would be
    // cut off in the select).
    options.push({ value: `-score:${thesis}`, label: "Thesis score, highest first" });
  }
  if (ordering && ordering !== "symbol" && !options.some((o) => o.value === ordering)) {
    const descending = ordering.startsWith("-");
    const key = descending ? ordering.slice(1) : ordering;
    const label = key.startsWith("score:")
      ? `Score on ${name(key.slice("score:".length))}, ${descending ? "highest" : "lowest"} first`
      : ORDERING_LABELS[ordering];
    options.push({ value: ordering, label });
  }
  return options;
}
